import { RefObject, useEffect, useMemo, useRef, useState } from 'react';

interface UseInViewOptions {
  /**
   * Offset from the top of the viewport before triggering.
   * Can be a string like "200px" or a number in pixels.
   * Default: 0
   */
  topOffset?: string | number;
  /**
   * Whether to trigger immediately if element is already in view on mount.
   * Default: true
   */
  triggerOnMount?: boolean;
  /**
   * Threshold for intersection (0-1).
   * Default: 0 (triggers when any part of element enters viewport)
   */
  threshold?: number;
}

/**
 * Custom hook to detect when an element enters the viewport.
 * Properly handles mount detection (unlike react-waypoint).
 * Uses Intersection Observer API for efficient viewport detection.
 *
 * @param ref - React ref to the element to observe
 * @param options - Configuration options
 * @returns boolean indicating if element is in view
 */
export function useInView(ref: RefObject<HTMLElement>, options: UseInViewOptions = {}) {
  const { topOffset = 0, triggerOnMount = true, threshold = 0 } = options;
  const [isIntersecting, setIsIntersecting] = useState(false);
  const [element, setElement] = useState<HTMLElement | null>(null);
  const hasTriggeredRef = useRef(false);
  const isClient = typeof window !== 'undefined';

  // Track when ref.current becomes available
  useEffect(() => {
    setElement(ref.current);
  }, [ref]);

  // Parse topOffset to pixels
  const topOffsetPx = useMemo(() => {
    if (typeof topOffset === 'number') {
      return topOffset;
    }
    if (typeof topOffset === 'string') {
      const match = topOffset.match(/^(\d+(?:\.\d+)?)(px)?$/i);
      if (match) {
        return parseFloat(match[1]);
      }
    }
    return 0;
  }, [topOffset]);

  // Create rootMargin from topOffset
  // Negative top margin expands viewport upward, so we trigger when element is topOffsetPx from top
  const rootMargin = useMemo(() => {
    if (topOffsetPx === 0) {
      return undefined;
    }
    return `-${topOffsetPx}px 0px 0px 0px`;
  }, [topOffsetPx]);

  const observer = useMemo(() => {
    if (!isClient) {
      return null;
    }

    const ioConfiguration: IntersectionObserverInit = {
      threshold,
    };

    if (rootMargin) {
      ioConfiguration.rootMargin = rootMargin;
    }

    return new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsIntersecting(true);
      } else {
        setIsIntersecting(false);
      }
    }, ioConfiguration);
  }, [isClient, threshold, rootMargin]);

  useEffect(() => {
    if (!element || !observer) {
      return () => {
        // No-op cleanup when element or observer is not available
      };
    }

    // Check initial visibility on mount if triggerOnMount is true
    const checkInitialVisibility = () => {
      if (triggerOnMount && !hasTriggeredRef.current) {
        const rect = element.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const offset = topOffsetPx;

        // Check if element intersects with the expanded viewport (considering topOffset)
        // With rootMargin of -200px, viewport effectively starts 200px above
        // Element is in view if it intersects: bottom > -offset AND top < viewportHeight
        const expandedViewportTop = -offset;
        const isInView = rect.bottom > expandedViewportTop && rect.top < viewportHeight;

        if (isInView) {
          setIsIntersecting(true);
          hasTriggeredRef.current = true;
        }
      }
    };

    // Check immediately
    checkInitialVisibility();

    // Also check after a short delay to catch cases where layout hasn't completed
    const timeoutId = setTimeout(checkInitialVisibility, 0);

    observer.observe(element);

    return () => {
      clearTimeout(timeoutId);
      observer.unobserve(element);
    };
  }, [element, observer, triggerOnMount, topOffsetPx]);

  return isIntersecting;
}
